import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { ReferenceMeasureData } from "@project/floorplanner/konva/reference-measure-konva";
import { Exclude, Expose } from "class-transformer";
import { IsNumber } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export class ReferenceMeasure extends FloorplanItem {
  @IsNumber()
  @Expose({ name: "scale" })
  private _scale: number;

  @IsNumber()
  @Expose({ name: "width" })
  private _width: number;

  @IsNumber()
  @Expose({ name: "height" })
  private _height: number;

  @IsNumber()
  @Expose({ name: "rotation" })
  private _rotation: number;

  @Exclude()
  public lengthChanged: Subject<{ currentData: ReferenceMeasureData; pendingData: ReferenceMeasureData }>;

  constructor(
    floorplan: Floorplan,
    id: string,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
    scale: number,
  ) {
    super(floorplan, id, x, y);
    this._scale = scale;
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.lengthChanged = new Subject();
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get rotation(): number {
    return this._rotation;
  }

  get scale(): number {
    return this._scale;
  }

  set scale(scale: number) {
    if (this._scale === scale) return;
    this._scale = scale;
    this._floorplan.publishUpdate(FloorplanEventType.REFERENCE_MEASURE_UPDATED, this);
  }

  override createDuplicateSnapshot(): ((x: number, y: number) => FloorplanItem) | undefined {
    return undefined;
  }

  override delete() {
    this.lengthChanged.unsubscribe();
    this._floorplan.deleteReferenceMeasure(this);
  }

  lengthChangedKonva(currentData: ReferenceMeasureData, pendingData: ReferenceMeasureData) {
    this.lengthChanged.next({ currentData: currentData, pendingData: pendingData });
  }

  updateForm(width: number, height: number, rotation: number, x: number, y: number) {
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this._floorplan.publishUpdate(FloorplanEventType.REFERENCE_MEASURE_UPDATED, this);
    this.updatePosition(x, y);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.referenceMeasureItemTextVisible;
  }

  resetForm(currentData: ReferenceMeasureData) {
    this._width = currentData.width;

    this._height = currentData.height;
    this._rotation = currentData.rotation;
    this._floorplan.publishUpdate(FloorplanEventType.REFERENCE_MEASURE_RESET, { floorplanItem: this, currentData: currentData });
    this.updatePosition(currentData.x, currentData.y);
  }

  static create(floorplan: Floorplan, x: number, y: number, width: number, height: number, rotation: number, scale: number) {
    return new ReferenceMeasure(floorplan, uuidv4(), x, y, width, height, rotation, scale);
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshReferenceMeasureLock();
  }
}
